import { FederatedPointerEvent, Graphics, type Container } from 'pixi.js';
import { type ResizeHandle, type IShape, type ShapeTransform } from './shapes/interfaces/IShape';
import { ViewportManager } from './ViewportManager';
import type { CommandManager } from './CommandManager';
import { AddShape } from './commands/AddShape';
import { RemoveShape } from './commands/RemoveShape';
import { ResizeShape } from './commands/ResizeShape';
import { MoveShape } from './commands/MoveShape';
import { RotateShape } from './commands/RotateShape';
import { TransformShapes } from './commands/TransformShapes';
import { RemoveShapes } from './commands/RemoveShapes';
import { calculateResizeTransform } from './resizeMath';
import { EventEmitter } from './EventEmitter';

export class ShapeManager {
    private container: Container;
    private shapes: IShape[] = [];
    private selectedShape: IShape | null = null;
    private selectedShapes: Set<IShape> = new Set();
    private isDragging: boolean = false;
    private isResizing: boolean = false;
    private isRotating: boolean = false;
    private viewportManager: ViewportManager;
    private pendingDragUpdate: boolean = false;
    private pendingResizeUpdate: boolean = false;
    private pendingRotateUpdate: boolean = false;
    private lastDragEvent: PointerEvent | null = null;
    private lastResizeEvent: PointerEvent | null = null;
    private lastRotateEvent: PointerEvent | null = null;
    public readonly commandManager: CommandManager;
    private currentResizeHandle: ResizeHandle | null = null;
    private resizeStartTransform: ShapeTransform = { x: 0, y: 0, width: 100, height: 100 };
    private currentHoveredHandle: ResizeHandle | null = null;
    private hoveringResizeHandle: boolean = false;
    private hoveringRotateHandle: boolean = false;
    private dragStartPosition: { x: number; y: number } = { x: 0, y: 0 };
    private rotateStartAngle: number = 0;
    private rotateStartRotation: number = 0;
    private rotateCenter: { x: number; y: number } = { x: 0, y: 0 };
    private readonly emitter: EventEmitter = new EventEmitter();
    private typeCounters: Record<string, number> = {};
    private selectionEnabled: boolean = true;
    private selectionOverlay: Graphics;
    private marqueeOverlay: Graphics;
    private isGroupDragging: boolean = false;
    private isGroupResizing: boolean = false;
    private isGroupRotating: boolean = false;
    private isTransformMode: boolean = false;
    private groupStartTransforms: Map<IShape, ShapeTransform> = new Map();
    private groupStartBounds: ShapeTransform | null = null;
    private groupResizeHandle: ResizeHandle | null = null;
    private groupDragStartWorld: { x: number; y: number } = { x: 0, y: 0 };

    private static readonly ROTATE_HANDLE_OFFSET = 30;

    // Curved-arrow rotation cursor as an inline SVG data URL
    private static readonly ROTATE_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cpath d='M10 2 A8 8 0 1 1 2.93 14.5' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round'/%3E%3Cpath d='M10 2 A8 8 0 1 1 2.93 14.5' fill='none' stroke='%23333' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpolygon points='10,2 6,6 14,6' fill='white'/%3E%3Cpolygon points='10,2 6.5,5.5 13.5,5.5' fill='%23333'/%3E%3C/svg%3E") 10 10, crosshair`;

    // Stored bound handler — required for removeEventListener to match the registered ref
    private readonly _onPointerMove = (e: PointerEvent) => this.handlePointerMove(e);

    constructor(container: Container, viewportManager: ViewportManager, commandManager: CommandManager) {
        this.container = container;
        this.viewportManager = viewportManager;
        this.commandManager = commandManager;
        this.selectionOverlay = new Graphics();
        this.marqueeOverlay = new Graphics();
        this.container.addChild(this.selectionOverlay);
        this.container.addChild(this.marqueeOverlay);
        this.init();
    }

    public init(): void {
        this.setupContainerInteraction();

        // Listen for viewport changes
        this.viewportManager.on('zoomed', this.onViewportZoomed.bind(this));
    }

    private setupContainerInteraction(): void {
        this.container.eventMode = 'static';

        // Use native pointer events for better tracking
        window.addEventListener('pointermove', this._onPointerMove, {
            passive: true,
            capture: true
        });
    }

    private handlePointerMove(event: PointerEvent): void {
        // Handle cursor changes for resize/rotate handles
        this.updateCursorForResizeHandles(event);

        if (this.isDragging && this.selectedShape) {
            this.lastDragEvent = event;

            // Only schedule one update per frame
            if (!this.pendingDragUpdate) {
                this.pendingDragUpdate = true;
                requestAnimationFrame(() => {
                    this.processDragUpdate();
                });
            }
        } else if (this.isResizing && this.selectedShape) {
            this.lastResizeEvent = event;

            // Only schedule one update per frame
            if (!this.pendingResizeUpdate) {
                this.pendingResizeUpdate = true;
                requestAnimationFrame(() => {
                    this.processResizeUpdate();
                });
            }
        } else if (this.isRotating && this.selectedShape) {
            this.lastRotateEvent = event;

            if (!this.pendingRotateUpdate) {
                this.pendingRotateUpdate = true;
                requestAnimationFrame(() => {
                    this.processRotateUpdate();
                });
            }
        }
    }

    private updateCursorForResizeHandles(event: PointerEvent): void {
        if (this.isGroupDragging || this.isGroupResizing || this.isGroupRotating) {
            return;
        }

        if (this.selectedShapes.size > 1 && this.isTransformMode) {
            this.updateCursorForGroupHandles(event);
            return;
        }

        if (!this.selectedShape || this.isDragging || this.isResizing || this.isRotating) {
            // Don't change cursor during drag/resize/rotate operations
            this.currentHoveredHandle = null;
            this.hoveringResizeHandle = false;
            this.hoveringRotateHandle = false;
            return;
        }

        // Create a mock FederatedPointerEvent for handle hit-testing
        const canvas = this.viewportManager.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const mockEvent = {
            global: { x: event.clientX - rect.left, y: event.clientY - rect.top },
            clientX: event.clientX,
            clientY: event.clientY,
            stopPropagation: () => {},
        } as FederatedPointerEvent;

        // Rotate handle takes priority over resize handles
        const isRotate = this.isRotateHandleAt(mockEvent);
        if (isRotate !== this.hoveringRotateHandle) {
            this.hoveringRotateHandle = isRotate;
            if (!isRotate) this.clearRotateCursor();
        }
        if (isRotate) {
            this.setRotateCursor();
            this.currentHoveredHandle = null;
            this.hoveringResizeHandle = false;
            return;
        }

        const hoveredHandle = this.getResizeHandleAt(mockEvent);

        if (hoveredHandle !== this.currentHoveredHandle) {
            this.currentHoveredHandle = hoveredHandle;
            this.hoveringResizeHandle = !!hoveredHandle;
            this.setCursorForHandle(hoveredHandle);
        }
    }

    private setCursorForHandle(handle: ResizeHandle | null): void {
        if (!handle) {
            this.setCursor('default');
            return;
        }

        let cursor: string;
        switch (handle) {
            case 'top-left':
            case 'bottom-right':
                cursor = 'nw-resize';
                break;
            case 'top-right':
            case 'bottom-left':
                cursor = 'ne-resize';
                break;
            case 'top':
            case 'bottom':
                cursor = 'ns-resize';
                break;
            case 'left':
            case 'right':
                cursor = 'ew-resize';
                break;
            default:
                cursor = 'default';
        }

        this.setCursor(cursor);
    }

    private updateCursorForGroupHandles(event: PointerEvent): void {
        const bounds = this.getSelectionBounds();
        if (!bounds) {
            this.setCursor('default');
            return;
        }

        const point = this.pointerEventToWorld(event);
        if (this.isGroupRotateHandleAt(point, bounds)) {
            this.setCursor(ShapeManager.ROTATE_CURSOR);
            return;
        }

        const handle = this.getGroupResizeHandleAt(point, bounds);
        if (handle) {
            this.setCursorForHandle(handle);
            return;
        }

        const insideBounds =
            point.x >= bounds.x &&
            point.x <= bounds.x + bounds.width &&
            point.y >= bounds.y &&
            point.y <= bounds.y + bounds.height;

        this.setCursor(insideBounds ? 'move' : 'default');
    }

    private setCursor(cursor: string): void {
        document.body.style.cursor = cursor;
    }

    private setRotateCursor(): void {
        // Must set cursor on the PixiJS container so PixiJS doesn't override
        // document.body.style.cursor with its own pointer-event cursor management.
        document.body.style.cursor = ShapeManager.ROTATE_CURSOR;
        if (this.selectedShape) {
            this.selectedShape.container.cursor = ShapeManager.ROTATE_CURSOR;
        }
    }

    private clearRotateCursor(): void {
        if (this.selectedShape) {
            this.selectedShape.container.cursor = 'pointer';
        }
    }

    private processDragUpdate(): void {
        this.pendingDragUpdate = false;

        if (this.lastDragEvent && this.selectedShape) {
            const canvas = this.viewportManager.getCanvas();
            const rect = canvas.getBoundingClientRect();
            const pixiEvent = {
                global: {
                    x: this.lastDragEvent.clientX - rect.left,
                    y: this.lastDragEvent.clientY - rect.top
                },
                clientX: this.lastDragEvent.clientX,
                clientY: this.lastDragEvent.clientY,
                stopPropagation: () => {},
            } as FederatedPointerEvent;

            this.selectedShape.onDrag(pixiEvent);
        }
    }

    private processResizeUpdate(): void {
        this.pendingResizeUpdate = false;

        if (!this.lastResizeEvent || !this.selectedShape || !this.currentResizeHandle) return;

        const canvas = this.viewportManager.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const { x: mouseWorldX, y: mouseWorldY } = this.viewportManager.screenToWorld(
            this.lastResizeEvent.clientX - rect.left,
            this.lastResizeEvent.clientY - rect.top
        );

        const result = calculateResizeTransform(
            this.resizeStartTransform,
            mouseWorldX,
            mouseWorldY,
            this.currentResizeHandle
        );
        this.selectedShape.position(result.x, result.y);
        this.selectedShape.setSize(result.width, result.height);
    }

    // Handle clicks on empty canvas space
    public handleCanvasClick(event: FederatedPointerEvent): void {
        // Always check handles first — for shapes like ImageShape the rotate/resize
        // handles are child elements that capture the event before it reaches the
        // viewport container, so we can't gate this check on event.target.
        if (this.selectedShape) {
            const resizeHandle = this.getResizeHandleAt(event);
            if (resizeHandle) {
                this.startResize(event, resizeHandle);
                return;
            }
            if (this.isRotateHandleAt(event)) {
                this.startRotate(event);
                return;
            }
        }

        // Deselect only when clicking on empty canvas (not on a shape or its children)
        if (event.target === this.container || event.target === this.container.parent) {
            this.deselectCurrentShape();
        }
    }

    public deselectCurrentShape(): void {
        if (this.selectedShapes.size > 0) {
            this.isTransformMode = false;
            this.selectedShapes.forEach(shape => shape.deselect());
            this.selectedShapes.clear();
            this.selectedShape = null;
            this.drawSelectionOverlay();
            this.emitter.emit('shapeDeselected');
            this.emitter.emit('selectionChanged', this.getSelectedShapes());
        }
        this.setCursor('default');
        this.currentHoveredHandle = null;
    }

    public addShape(shape: IShape): void {
        const command = new AddShape(this, shape);
        this.commandManager.execute(command);
    }

    public on(event: string, callback: Function): void {
        this.emitter.on(event, callback);
    }

    public setSelectionEnabled(enabled: boolean): void {
        this.selectionEnabled = enabled;
    }

    private generateName(type: string): string {
        const base = type.charAt(0).toUpperCase() + type.slice(1);
        this.typeCounters[type] = (this.typeCounters[type] ?? 0) + 1;
        return `${base} ${this.typeCounters[type]}`;
    }

    public _addShape(shape: IShape): void {
        if (!shape.name || shape.name === 'Image' || shape.name === 'Shape') {
            shape.name = this.generateName(shape.type);
        }
        this.shapes.push(shape);
        this.container.addChild(shape.container);
        this.bringSelectionUiToFront();
        this.emitter.emit('shapeAdded', shape);

        // Add shape-specific event handlers
        shape.container
            .on('pointerdown', (event: FederatedPointerEvent) => {
                this.handleShapeClick(shape, event);
            }).on('pointerup', (event: FederatedPointerEvent) => {
                this.handleShapeClick(shape, event);
            });
    }

    private handleShapeClick(shape: IShape, event: FederatedPointerEvent): void {
        if (!this.selectionEnabled) return;
        if (this.selectedShapes.size > 1 && this.selectedShapes.has(shape)) return;
        // First, ensure the shape is selected
        if (this.selectedShape !== shape && event.type === 'pointerdown') {
            if(!event.shiftKey) {
                this.deselectCurrentShape();
            }
            this.addShapeToSelection(shape);
        } else if (this.selectedShape === shape && event.type === 'pointerup') {
            this.endDrag();
        }
    }

    private getResizeHandleAt(event: FederatedPointerEvent): ResizeHandle | null {
        if (!this.selectedShape) return null;

        const bounds = this.selectedShape.getBounds();
        const handleSize = 16;
        const margin = 8;

        // Convert global coordinates to local shape coordinates
        const localPos = this.selectedShape.container.toLocal(event.global);

        // First check corner handles (they have priority)
        const cornerHandles = [
            { type: 'top-left' as ResizeHandle, x: -margin, y: -margin },
            { type: 'top-right' as ResizeHandle, x: bounds.width + margin, y: -margin },
            { type: 'bottom-left' as ResizeHandle, x: -margin, y: bounds.height + margin },
            { type: 'bottom-right' as ResizeHandle, x: bounds.width + margin, y: bounds.height + margin }
        ];

        for (const handle of cornerHandles) {
            const distance = Math.sqrt(
                Math.pow(localPos.x - handle.x, 2) + Math.pow(localPos.y - handle.y, 2)
            );
            if (distance <= handleSize) {
                return handle.type;
            }
        }

        // Then check edge handles with better edge detection
        const edgeHandles = [
            {
                type: 'top' as ResizeHandle,
                x: bounds.width / 2,
                y: -margin,
                check: (x: number, y: number) => {
                    return Math.abs(y - (-margin)) <= handleSize &&
                           x >= -margin && x <= bounds.width + margin;
                }
            },
            {
                type: 'bottom' as ResizeHandle,
                x: bounds.width / 2,
                y: bounds.height + margin,
                check: (x: number, y: number) => {
                    return Math.abs(y - (bounds.height + margin)) <= handleSize &&
                           x >= -margin && x <= bounds.width + margin;
                }
            },
            {
                type: 'left' as ResizeHandle,
                x: -margin,
                y: bounds.height / 2,
                check: (x: number, y: number) => {
                    return Math.abs(x - (-margin)) <= handleSize &&
                           y >= -margin && y <= bounds.height + margin;
                }
            },
            {
                type: 'right' as ResizeHandle,
                x: bounds.width + margin,
                y: bounds.height / 2,
                check: (x: number, y: number) => {
                    return Math.abs(x - (bounds.width + margin)) <= handleSize &&
                           y >= -margin && y <= bounds.height + margin;
                }
            }
        ];

        for (const handle of edgeHandles) {
            if (handle.check(localPos.x, localPos.y)) {
                return handle.type;
            }
        }

        return null;
    }

    private bringSelectionUiToFront(): void {
        const canSetChildIndex = typeof (this.container as any).setChildIndex === 'function';
        if (!canSetChildIndex) return;
        if (this.selectionOverlay.parent === this.container) {
            this.container.setChildIndex(this.selectionOverlay, this.container.children.length - 1);
        }
        if (this.marqueeOverlay.parent === this.container) {
            this.container.setChildIndex(this.marqueeOverlay, this.container.children.length - 1);
        }
    }

    private getShapeTransformRect(shape: IShape): ShapeTransform {
        return this.getShapeVisualBounds(shape);
    }

    private rectsIntersect(a: ShapeTransform, b: ShapeTransform): boolean {
        return (
            a.x < b.x + b.width &&
            a.x + a.width > b.x &&
            a.y < b.y + b.height &&
            a.y + a.height > b.y
        );
    }

    private getShapeVisualBounds(shape: IShape): ShapeTransform {
        const transform = shape.getTransform();
        const rotation = transform.rotation ?? 0;
        if (rotation === 0) return transform;

        const centerX = transform.x + transform.width / 2;
        const centerY = transform.y + transform.height / 2;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const corners = [
            { x: transform.x, y: transform.y },
            { x: transform.x + transform.width, y: transform.y },
            { x: transform.x + transform.width, y: transform.y + transform.height },
            { x: transform.x, y: transform.y + transform.height },
        ].map(point => {
            const dx = point.x - centerX;
            const dy = point.y - centerY;
            return {
                x: centerX + dx * cos - dy * sin,
                y: centerY + dx * sin + dy * cos,
            };
        });

        const left = Math.min(...corners.map(point => point.x));
        const top = Math.min(...corners.map(point => point.y));
        const right = Math.max(...corners.map(point => point.x));
        const bottom = Math.max(...corners.map(point => point.y));
        return { x: left, y: top, width: right - left, height: bottom - top, rotation: 0 };
    }

    private getSelectionBounds(): ShapeTransform | null {
        const selected = this.getSelectedShapes();
        if (selected.length === 0) return null;

        const rects = selected.map(shape => this.getShapeVisualBounds(shape));
        const left = Math.min(...rects.map(rect => rect.x));
        const top = Math.min(...rects.map(rect => rect.y));
        const right = Math.max(...rects.map(rect => rect.x + rect.width));
        const bottom = Math.max(...rects.map(rect => rect.y + rect.height));
        return { x: left, y: top, width: right - left, height: bottom - top, rotation: 0 };
    }

    private drawSelectionOverlay(): void {
        this.selectionOverlay.clear();
        const bounds = this.getSelectionBounds();
        if (!bounds || this.selectedShapes.size <= 1) return;

        if (!this.isTransformMode) {
            this.drawSelectedItemOutlines();
            this.bringSelectionUiToFront();
            return;
        }

        this.selectionOverlay.setStrokeStyle({ width: 1.5, color: 0x2563eb, alpha: 0.45 });
        this.selectionOverlay.rect(bounds.x, bounds.y, bounds.width, bounds.height);
        this.selectionOverlay.stroke();
        this.drawGroupHandles(bounds);
        this.bringSelectionUiToFront();
    }

    private drawSelectedItemOutlines(): void {
        const viewportScale = this.container.scale.x || 1;
        this.selectionOverlay.setStrokeStyle({ width: Math.max(2 / viewportScale, 1.5), color: 0x2563eb, alpha: 0.84 });
        this.getSelectedShapes().forEach(shape => {
            const rect = this.getShapeVisualBounds(shape);
            this.selectionOverlay.rect(rect.x, rect.y, rect.width, rect.height);
        });
        this.selectionOverlay.stroke();
    }

    private drawGroupHandles(bounds: ShapeTransform): void {
        const viewportScale = this.container.scale.x || 1;
        const handleSize = Math.max(8 / viewportScale, 6);
        const half = handleSize / 2;
        const handles = [
            [bounds.x, bounds.y],
            [bounds.x + bounds.width, bounds.y],
            [bounds.x, bounds.y + bounds.height],
            [bounds.x + bounds.width, bounds.y + bounds.height],
            [bounds.x + bounds.width / 2, bounds.y],
            [bounds.x + bounds.width / 2, bounds.y + bounds.height],
            [bounds.x, bounds.y + bounds.height / 2],
            [bounds.x + bounds.width, bounds.y + bounds.height / 2],
        ];
        this.selectionOverlay.fill({ color: 0x2563eb, alpha: 0.55 });
        handles.forEach(([x, y]) => this.selectionOverlay.rect(x - half, y - half, handleSize, handleSize));
        this.selectionOverlay.fill();

        const rotateY = bounds.y - Math.max(28 / viewportScale, 20);
        this.selectionOverlay.setStrokeStyle({ width: 1.2 / viewportScale, color: 0x2563eb, alpha: 0.45 });
        this.selectionOverlay.moveTo(bounds.x + bounds.width / 2, bounds.y);
        this.selectionOverlay.lineTo(bounds.x + bounds.width / 2, rotateY);
        this.selectionOverlay.stroke();
        this.selectionOverlay.fill({ color: 0x2563eb, alpha: 0.55 });
        this.selectionOverlay.circle(bounds.x + bounds.width / 2, rotateY, Math.max(5 / viewportScale, 4));
        this.selectionOverlay.fill();
    }

    private federatedEventToWorld(event: FederatedPointerEvent): { x: number; y: number } {
        return this.viewportManager.screenToWorld(event.global.x, event.global.y);
    }

    private pointerEventToWorld(event: PointerEvent): { x: number; y: number } {
        const canvas = this.viewportManager.getCanvas();
        const rect = canvas.getBoundingClientRect();
        return this.viewportManager.screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
    }

    public drawMarquee(start: { x: number; y: number }, end: { x: number; y: number }): void {
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const width = Math.abs(end.x - start.x);
        const height = Math.abs(end.y - start.y);
        this.marqueeOverlay.clear();
        this.marqueeOverlay.fill({ color: 0x2563eb, alpha: 0.08 });
        this.marqueeOverlay.rect(x, y, width, height);
        this.marqueeOverlay.fill();
        this.marqueeOverlay.setStrokeStyle({ width: 1, color: 0x2563eb, alpha: 0.7 });
        this.marqueeOverlay.rect(x, y, width, height);
        this.marqueeOverlay.stroke();
        this.bringSelectionUiToFront();
    }

    public clearMarquee(): void {
        this.marqueeOverlay.clear();
    }

    public selectShapesInRect(start: { x: number; y: number }, end: { x: number; y: number }, additive: boolean = false): void {
        const rect = {
            x: Math.min(start.x, end.x),
            y: Math.min(start.y, end.y),
            width: Math.abs(end.x - start.x),
            height: Math.abs(end.y - start.y),
        };
        const hits = this.shapes.filter(shape => this.rectsIntersect(this.getShapeTransformRect(shape), rect));
        this.selectShapes(hits, additive);
    }

    private startResize(event: FederatedPointerEvent, handle: ResizeHandle): void {
        if (!this.selectedShape) return;

        this.isResizing = true;
        this.currentResizeHandle = handle;
        this.resizeStartTransform = this.selectedShape.getTransform();
        this.selectedShape.startResize(event, handle);
    }

    private isRotateHandleAt(event: FederatedPointerEvent): boolean {
        if (!this.selectedShape) return false;
        const bounds = this.selectedShape.getBounds();
        const localPos = this.selectedShape.container.toLocal(event.global);
        const viewportScale = this.selectedShape.container.parent?.scale.x || 1;
        const offsetY = Math.max(ShapeManager.ROTATE_HANDLE_OFFSET / viewportScale, 24);
        const dx = localPos.x - bounds.width / 2;
        const dy = localPos.y - (-offsetY);
        return Math.sqrt(dx * dx + dy * dy) <= 12;
    }

    private startRotate(event: FederatedPointerEvent): void {
        if (!this.selectedShape) return;
        this.isRotating = true;
        this.setRotateCursor();
        this.rotateStartRotation = this.selectedShape.container.rotation;
        // container.x/y is the pivot point (shape centre) in world space
        this.rotateCenter = {
            x: this.selectedShape.container.x,
            y: this.selectedShape.container.y,
        };
        const mouse = this.viewportManager.screenToWorld(event.global.x, event.global.y);
        this.rotateStartAngle = Math.atan2(
            mouse.y - this.rotateCenter.y,
            mouse.x - this.rotateCenter.x,
        );
    }

    private processRotateUpdate(): void {
        this.pendingRotateUpdate = false;
        if (!this.lastRotateEvent || !this.selectedShape) return;

        const canvas = this.viewportManager.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const mouse = this.viewportManager.screenToWorld(
            this.lastRotateEvent.clientX - rect.left,
            this.lastRotateEvent.clientY - rect.top,
        );
        const newAngle = Math.atan2(
            mouse.y - this.rotateCenter.y,
            mouse.x - this.rotateCenter.x,
        );
        let rotation = this.rotateStartRotation + (newAngle - this.rotateStartAngle);
        if (this.lastRotateEvent.shiftKey) {
            const snapRad = (15 * Math.PI) / 180;
            rotation = Math.round(rotation / snapRad) * snapRad;
        }
        this.selectedShape.container.rotation = rotation;
    }

    public handleGroupPointerDown(event: FederatedPointerEvent): boolean {
        if (this.selectedShapes.size <= 1 || !this.isTransformMode) return false;
        const bounds = this.getSelectionBounds();
        if (!bounds) return false;
        const point = this.federatedEventToWorld(event);
        if (this.isGroupRotateHandleAt(point, bounds)) {
            this.startGroupRotate(point, bounds);
            event.stopPropagation();
            return true;
        }
        const handle = this.getGroupResizeHandleAt(point, bounds);
        if (handle) {
            this.startGroupResize(bounds, handle);
            event.stopPropagation();
            return true;
        }
        if (
            point.x >= bounds.x &&
            point.x <= bounds.x + bounds.width &&
            point.y >= bounds.y &&
            point.y <= bounds.y + bounds.height
        ) {
            this.startGroupDrag(point);
            event.stopPropagation();
            return true;
        }
        return false;
    }

    private getGroupResizeHandleAt(point: { x: number; y: number }, bounds: ShapeTransform): ResizeHandle | null {
        const viewportScale = this.container.scale.x || 1;
        const hit = Math.max(12 / viewportScale, 8);
        const handles: Array<{ handle: ResizeHandle; x: number; y: number }> = [
            { handle: 'top-left', x: bounds.x, y: bounds.y },
            { handle: 'top-right', x: bounds.x + bounds.width, y: bounds.y },
            { handle: 'bottom-left', x: bounds.x, y: bounds.y + bounds.height },
            { handle: 'bottom-right', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
            { handle: 'top', x: bounds.x + bounds.width / 2, y: bounds.y },
            { handle: 'bottom', x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
            { handle: 'left', x: bounds.x, y: bounds.y + bounds.height / 2 },
            { handle: 'right', x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
        ];
        const found = handles.find(item => Math.hypot(point.x - item.x, point.y - item.y) <= hit);
        return found?.handle ?? null;
    }

    private isGroupRotateHandleAt(point: { x: number; y: number }, bounds: ShapeTransform): boolean {
        const viewportScale = this.container.scale.x || 1;
        const rotateY = bounds.y - Math.max(28 / viewportScale, 20);
        return Math.hypot(point.x - (bounds.x + bounds.width / 2), point.y - rotateY) <= Math.max(12 / viewportScale, 8);
    }

    private snapshotGroupTransforms(): Map<IShape, ShapeTransform> {
        return new Map(this.getSelectedShapes().map(shape => [shape, { ...shape.getTransform() }]));
    }

    private startGroupDrag(point: { x: number; y: number }): void {
        this.isGroupDragging = true;
        this.groupDragStartWorld = point;
        this.groupStartTransforms = this.snapshotGroupTransforms();
    }

    public startSelectedGroupDragFromPointer(event: FederatedPointerEvent): boolean {
        if (this.selectedShapes.size <= 1 || this.isTransformMode) return false;
        const targetShape = this.shapes.find(shape => shape.container === event.target || shape.container.children.includes(event.target as any));
        if (!targetShape || !this.selectedShapes.has(targetShape)) return false;
        const point = this.federatedEventToWorld(event);
        this.startGroupDrag(point);
        return true;
    }

    private startGroupResize(bounds: ShapeTransform, handle: ResizeHandle): void {
        this.isGroupResizing = true;
        this.groupResizeHandle = handle;
        this.groupStartBounds = { ...bounds };
        this.groupStartTransforms = this.snapshotGroupTransforms();
    }

    private startGroupRotate(point: { x: number; y: number }, bounds: ShapeTransform): void {
        this.isGroupRotating = true;
        this.groupStartBounds = { ...bounds };
        this.groupStartTransforms = this.snapshotGroupTransforms();
        this.rotateCenter = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
        this.rotateStartAngle = Math.atan2(point.y - this.rotateCenter.y, point.x - this.rotateCenter.x);
    }

    public updateGroupTransformFromPointer(event: PointerEvent): boolean {
        if (!this.isGroupDragging && !this.isGroupResizing && !this.isGroupRotating) return false;
        const point = this.pointerEventToWorld(event);

        if (this.isGroupDragging) {
            const dx = point.x - this.groupDragStartWorld.x;
            const dy = point.y - this.groupDragStartWorld.y;
            this.groupStartTransforms.forEach((t, shape) => shape.position(t.x + dx, t.y + dy));
        } else if (this.isGroupResizing && this.groupStartBounds && this.groupResizeHandle) {
            const nextBounds = calculateResizeTransform(this.groupStartBounds, point.x, point.y, this.groupResizeHandle);
            const sx = nextBounds.width / this.groupStartBounds.width;
            const sy = nextBounds.height / this.groupStartBounds.height;
            this.groupStartTransforms.forEach((t, shape) => {
                const relX = t.x - this.groupStartBounds!.x;
                const relY = t.y - this.groupStartBounds!.y;
                shape.setTransform({
                    ...t,
                    x: nextBounds.x + relX * sx,
                    y: nextBounds.y + relY * sy,
                    width: t.width * sx,
                    height: t.height * sy,
                });
            });
        } else if (this.isGroupRotating) {
            const angle = Math.atan2(point.y - this.rotateCenter.y, point.x - this.rotateCenter.x);
            let delta = angle - this.rotateStartAngle;
            if (event.shiftKey) {
                const snapRad = (15 * Math.PI) / 180;
                delta = Math.round(delta / snapRad) * snapRad;
            }
            this.groupStartTransforms.forEach((t, shape) => {
                const cx = t.x + t.width / 2;
                const cy = t.y + t.height / 2;
                const dx = cx - this.rotateCenter.x;
                const dy = cy - this.rotateCenter.y;
                const rotatedX = this.rotateCenter.x + dx * Math.cos(delta) - dy * Math.sin(delta);
                const rotatedY = this.rotateCenter.y + dx * Math.sin(delta) + dy * Math.cos(delta);
                shape.setTransform({
                    ...t,
                    x: rotatedX - t.width / 2,
                    y: rotatedY - t.height / 2,
                    rotation: (t.rotation ?? 0) + delta,
                });
            });
        }
        this.drawSelectionOverlay();
        return true;
    }

    public endGroupTransform(): void {
        if (!this.isGroupDragging && !this.isGroupResizing && !this.isGroupRotating) return;
        const transforms = this.getSelectedShapes().map(shape => ({
            shape,
            oldTransform: this.groupStartTransforms.get(shape)!,
            newTransform: { ...shape.getTransform() },
        })).filter(item => item.oldTransform);
        const changed = transforms.some(item => JSON.stringify(item.oldTransform) !== JSON.stringify(item.newTransform));
        if (changed) {
            this.commandManager.execute(new TransformShapes(transforms));
        }
        this.isGroupDragging = false;
        this.isGroupResizing = false;
        this.isGroupRotating = false;
        this.groupStartTransforms.clear();
        this.groupStartBounds = null;
        this.groupResizeHandle = null;
        this.drawSelectionOverlay();
    }

    public endRotate(): void {
        if (this.selectedShape && this.isRotating) {
            const newRotation = this.selectedShape.container.rotation;
            if (newRotation !== this.rotateStartRotation) {
                const command = new RotateShape(
                    this.selectedShape,
                    this.rotateStartRotation,
                    newRotation,
                );
                this.commandManager.execute(command);
            }
        }
        this.isRotating = false;
        this.lastRotateEvent = null;
        this.clearRotateCursor();
        this.setCursor('default');
    }

    // Start dragging the selected shape
    public startDragSelectedShape(event: FederatedPointerEvent): void {
        if (this.selectedShape && !this.hoveringResizeHandle && !this.hoveringRotateHandle && !this.isRotating) {
            const t = this.selectedShape.getTransform();
            this.dragStartPosition = { x: t.x, y: t.y };
            this.selectedShape.startDrag(event);
            this.isDragging = true;
        }
    }

    // End dragging
    public endDrag(): void {
        if (this.selectedShape && this.isDragging) {
            this.selectedShape.endDrag();

            const t = this.selectedShape.getTransform();
            const newX = t.x;
            const newY = t.y;
            const moved =
                newX !== this.dragStartPosition.x ||
                newY !== this.dragStartPosition.y;

            if (moved) {
                const command = new MoveShape(
                    this.selectedShape,
                    this.dragStartPosition.x,
                    this.dragStartPosition.y,
                    newX,
                    newY,
                );
                this.commandManager.execute(command);
            }
        }
        this.isDragging = false;
    }

    // End resizing
    public endResize(): void {
        if (this.selectedShape && this.isResizing) {
            const newTransform = this.selectedShape.getTransform();
            const changed =
                newTransform.width  !== this.resizeStartTransform.width  ||
                newTransform.height !== this.resizeStartTransform.height ||
                newTransform.x      !== this.resizeStartTransform.x     ||
                newTransform.y      !== this.resizeStartTransform.y;
            if (changed) {
                const command = new ResizeShape(this, this.selectedShape, this.resizeStartTransform, newTransform);
                this.commandManager.execute(command);
            }
            this.selectedShape.endResize();
        }
        this.isResizing = false;
        this.currentResizeHandle = null;
    }

    public getSelectedShape(): IShape | null {
        return this.selectedShape;
    }

    public getSelectedShapes(): IShape[] {
        return Array.from(this.selectedShapes);
    }

    public selectShape(shape: IShape): void {
        if (this.selectedShape === shape && this.selectedShapes.size === 1) return;
        this.deselectCurrentShape();
        this.addShapeToSelection(shape);
    }

    public selectShapes(shapes: IShape[], additive: boolean = false): void {
        if (!additive) {
            this.deselectCurrentShape();
        }
        this.isTransformMode = false;
        shapes.forEach(shape => this.addShapeToSelection(shape, false));
        this.selectedShape = shapes[shapes.length - 1] ?? this.selectedShape;
        this.syncSelectedShapeVisuals();
        this.drawSelectionOverlay();
        if (this.selectedShape) {
            this.emitter.emit('shapeSelected', this.selectedShape);
            this.emitter.emit('selectionChanged', this.getSelectedShapes());
        }
    }

    private addShapeToSelection(shape: IShape, emit: boolean = true): void {
        if (this.selectedShapes.has(shape)) return;
        this.isTransformMode = false;
        this.selectedShape = shape;
        this.selectedShapes.add(shape);
        this.syncSelectedShapeVisuals();
        this.drawSelectionOverlay();
        if (emit) this.emitter.emit('shapeSelected', shape);
        if (emit) this.emitter.emit('selectionChanged', this.getSelectedShapes());
    }

    private syncSelectedShapeVisuals(): void {
        const selected = this.getSelectedShapes();
        if (selected.length === 1) {
            selected[0].select();
            return;
        }

        selected.forEach(shape => {
            if (shape.selected) shape.deselect();
        });
    }

    public toggleTransformMode(): void {
        if (this.selectedShapes.size <= 1) {
            this.isTransformMode = false;
        } else {
            this.isTransformMode = !this.isTransformMode;
        }
        this.drawSelectionOverlay();
        this.emitter.emit('selectionChanged', this.getSelectedShapes());
    }

    public isSelectionTransformMode(): boolean {
        return this.isTransformMode;
    }

    public bringForward(shape: IShape): void {
        const idx = this.shapes.indexOf(shape);
        if (idx < this.shapes.length - 1) {
            this.shapes.splice(idx, 1);
            this.shapes.splice(idx + 1, 0, shape);
            this.container.setChildIndex(shape.container, idx + 1);
        }
    }

    public sendBack(shape: IShape): void {
        const idx = this.shapes.indexOf(shape);
        if (idx > 0) {
            this.shapes.splice(idx, 1);
            this.shapes.splice(idx - 1, 0, shape);
            this.container.setChildIndex(shape.container, idx - 1);
        }
    }

    public _removeShape(shape: IShape): void {
        if (this.selectedShapes.has(shape)) {
            this.selectedShapes.delete(shape);
            if (this.selectedShape === shape) {
                this.selectedShape = this.getSelectedShapes()[0] ?? null;
            }
            shape.deselect();
            this.drawSelectionOverlay();
            if (!this.selectedShape) this.emitter.emit('shapeDeselected');
        }
        shape.container.removeAllListeners();
        const idx = this.shapes.indexOf(shape);
        if (idx !== -1) this.shapes.splice(idx, 1);
        this.container.removeChild(shape.container);
        this.emitter.emit('shapeRemoved', shape);
    }

    public removeShape(shape: IShape): void {
        const command = new RemoveShape(this, shape);
        this.commandManager.execute(command);
    }

    public removeSelectedShapes(): void {
        const selected = this.getSelectedShapes();
        if (selected.length === 0) return;
        this.commandManager.execute(new RemoveShapes(this, selected));
    }

    public getShapes(): IShape[] {
        return this.shapes;
    }

    public clear(): void {
        this.selectedShape = null;
        this.selectedShapes.clear();
        this.shapes.forEach(shape => {
            this.container.removeChild(shape.container);
        });
        this.shapes = [];
        this.typeCounters = {};
        this.setCursor('default');
        this.currentHoveredHandle = null;
        this.selectionOverlay.clear();
        this.marqueeOverlay.clear();
    }

    private onViewportZoomed({ }: { scale: number }): void {
        // Redraw selected shape to update resize handles based on new zoom level
        this.getSelectedShapes().forEach(shape => {
            // Force redraw to update handle sizes
            if ((shape as any).needsRedraw !== undefined) {
                (shape as any).needsRedraw = true;
            }
            shape.draw();
        });
        this.drawSelectionOverlay();
    }

    public cleanup(): void {
        window.removeEventListener('pointermove', this._onPointerMove, { capture: true });
        this.setCursor('default');
        this.clear();
    }
}
